import { useUserStore } from "~/stores/userstore";
import { withLoading } from "~/helpers/withLoading";
import { withErrorMessage } from "~/helpers/withErrorMessage";
import { useUiStore } from "~/stores/ui/uistore";
import { useUserAccessTokenStore } from "~/stores/useraccesstokenstore";
import { useToastStore } from "~/stores/ui/toaststore";
import type { WebModelGetCompanyInvite } from "~/model/api/pLCnextStoreFrontendAPI.schemas";

export function useUserLoader() {
  const userStore = useUserStore();
  const userAppSubscriptionsStore = useUserAppSubscriptionsStore();
  const userAccessTokenStore = useUserAccessTokenStore();
  const uiStore = useUiStore();
  const toastStore = useToastStore();

  watch(
    () => userStore.authUserUuid,
    async (uuid) => {
      console.debug("authUserUuid changed:", uuid);
      if (!uuid) {
        await userStore.clearCookieAndLocalStorageAndState();
        await userStore.loadUserHub();
        userAppSubscriptionsStore.clear();
        userAccessTokenStore.clear();
        return;
      }
      try {
        await withLoading(
          Promise.all([
            withErrorMessage(userStore.loadUserRegistrationStatus()),
            withErrorMessage(userStore.loadAuthUser()),
            withErrorMessage(userStore.loadAuthUserRoles()),
            withErrorMessage(userStore.loadUserHub()),
            withErrorMessage(
              userAppSubscriptionsStore.loadUserAppSubscriptions(uuid),
            ),
            withErrorMessage(userAccessTokenStore.loadUserAPIAccessToken()),
          ]),
        );
      } catch (err) {
        console.error(err);
        await userStore.clearCookieAndLocalStorageAndState();
      }
    },
    { immediate: true },
  );

  watch(
    () => userStore.userHasCompanyInvite,
    async (_unusedHasInvite) => {
      const companyInvites = await userStore.loadUserCompanyInvites();
      if (companyInvites && companyInvites.length > 0) {
        const companyInvite = companyInvites[0] as WebModelGetCompanyInvite;
        uiStore.userCompanyInviteCode = companyInvite.code;
      } else {
        uiStore.userCompanyInviteCode = undefined;
      }
    },
    { immediate: true },
  );

  // Handle MFA requirement notification
  watch(
    () => userStore.userMfaRequired,
    (mfaRequired) => {
      if (!mfaRequired) return;

      toastStore.addToastWithMessageAndTitle(
        "Notice",
        "Your account is not secured by two-factor authentication (2FA). Navigate to your 'user profile' in the 'password' section to enable it.",
        "warning",
      );
    },
    { immediate: true },
  );
}
